import { AnimatePresence, motion } from 'framer-motion'
import type { AttentionReason, GateState } from 'eyecontact'
import { cn } from '../lib/cn'

const COPY: Record<AttentionReason, string> = {
  attentive: 'Eyes on screen',
  'no-face': 'No face in frame',
  'head-turned': 'Head turned away',
  'gaze-away': 'Looking elsewhere',
  blink: 'Blink',
  'tab-hidden': 'Tab in the background',
  'window-blurred': 'Switched to another app',
  'not-started': 'Camera off',
}

export function AttentionBadge({
  state,
  reason,
  awayForMs,
  graceMs,
}: {
  state: GateState
  reason: AttentionReason
  awayForMs: number
  graceMs: number
}) {
  const watching = state === 'attentive'
  const paused = state === 'away'
  const live = watching || paused

  // How much of the grace window has burned down. This is the single most
  // useful bit of feedback in the whole UI: you can see the pause coming.
  const burn = paused ? 1 : Math.min(1, awayForMs / Math.max(1, graceMs))

  return (
    <div
      className={cn(
        'inline-flex items-center gap-2.5 rounded-full border py-1 pl-1.5 pr-3 transition-colors',
        paused && 'border-warn/40 bg-warn/10',
        watching && 'border-accent/40 bg-accent/10',
        !live && 'border-line bg-surface-2',
      )}
      role="status"
      aria-live="polite"
    >
      <span className="relative grid h-4 w-4 place-items-center">
        {watching && (
          <span className="absolute inset-0 animate-pulse-ring rounded-full bg-accent/30" />
        )}
        <span
          className={cn(
            'h-2 w-2 rounded-full',
            paused && 'bg-warn',
            watching && 'bg-accent',
            !live && 'bg-faint',
          )}
        />
      </span>

      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={`${state}-${reason}`}
          initial={{ opacity: 0, y: 3 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -3 }}
          transition={{ duration: 0.14 }}
          className="text-xs font-medium"
        >
          {state === 'starting' && 'Starting camera…'}
          {state === 'error' && 'Camera error'}
          {state === 'idle' && 'Idle'}
          {live && (paused ? 'Paused — look back to resume' : COPY[reason])}
        </motion.span>
      </AnimatePresence>

      {live && (
        <span className="relative h-1 w-14 overflow-hidden rounded-full bg-surface-2" aria-hidden="true">
          <motion.span
            className={cn('absolute inset-y-0 left-0 rounded-full', paused ? 'bg-warn' : 'bg-accent')}
            animate={{ width: `${burn * 100}%` }}
            transition={{ duration: 0.15, ease: 'linear' }}
          />
        </span>
      )}
    </div>
  )
}
